import React, { useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { otaVersionsApi } from "../../api";
import { OTAComponent, OTAComponentVersionCreatePayload } from "../../types";

interface AddVersionModalProps {
  component: OTAComponent;
  onClose: () => void;
  onVersionCreated: () => void;
}

export const AddVersionModal: React.FC<AddVersionModalProps> = ({
  component,
  onClose,
  onVersionCreated,
}) => {
  const isContainer = component.type.toLowerCase() === "container";
  const isHost = component.type.toLowerCase() === "host";

  const [formData, setFormData] = useState<OTAComponentVersionCreatePayload>({
    componentId: component.id,
    versionNumber: "",
    description: "",
    tags: [],
    ...(isContainer
      ? { ccmImageTag: "" }
      : isHost
      ? { hostPackageVersion: "" }
      : { s3Uri: "", sha256Hash: "", firmwareVersion: "" }),
  });
  const [tagsInput, setTagsInput] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingSha256, setIsFetchingSha256] = useState(false);
  const [sha256FetchError, setSha256FetchError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateVersionFormat = (v: string) => /^\d+\.\d+\.\d+$/.test(v);

  const validateS3Uri = (uri: string) =>
    uri.startsWith("s3://") && uri.split("/").length > 3;

  const validateSha256Hash = (hash: string) => {
    const t = hash.trim();
    if (/^[0-9a-fA-F]{64}$/.test(t)) return true;
    const b64 = /^[A-Za-z0-9+/]+=*$/;
    return b64.test(t) && t.length >= 40 && t.length <= 48;
  };

  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleChange = (
    field: keyof OTAComponentVersionCreatePayload,
    value: string
  ) => {
    if (field === "s3Uri") {
      setSha256FetchError(null);
      setFormData((prev) => ({ ...prev, s3Uri: value, sha256Hash: "" }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    clearError(field);
  };

  const handleS3UriBlur = async () => {
    const uri = formData.s3Uri?.trim() ?? "";
    if (!validateS3Uri(uri)) return;
    setIsFetchingSha256(true);
    setSha256FetchError(null);
    try {
      const hash = await otaVersionsApi.fetchSha256(uri);
      setFormData((prev) => ({ ...prev, sha256Hash: hash }));
      clearError("sha256Hash");
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error ?? "Could not fetch hash from S3";
      setSha256FetchError(msg);
    } finally {
      setIsFetchingSha256(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.versionNumber) {
      newErrors.versionNumber = "Version number is required";
    } else if (!validateVersionFormat(formData.versionNumber)) {
      newErrors.versionNumber = "Version must be in format d.d.d (e.g., 1.0.4)";
    }

    if (isContainer) {
      if (!formData.ccmImageTag?.trim()) {
        newErrors.ccmImageTag = "Image tag is required (e.g. CSfa2e41dc)";
      }
    } else if (isHost) {
      if (!formData.hostPackageVersion?.trim()) {
        newErrors.hostPackageVersion =
          "Package version is required (e.g., 1.3.0~rc7)";
      }
    } else {
      if (!formData.s3Uri) {
        newErrors.s3Uri = "S3 URI is required";
      } else if (!validateS3Uri(formData.s3Uri)) {
        newErrors.s3Uri =
          "S3 URI must start with 's3://' and include bucket and key";
      }
      if (!formData.sha256Hash?.trim()) {
        newErrors.sha256Hash = "SHA-256 hash is required";
      } else if (!validateSha256Hash(formData.sha256Hash)) {
        newErrors.sha256Hash =
          "SHA-256 hash must be 64 hex characters or Base64 format from S3";
      }
      if (!formData.firmwareVersion?.trim()) {
        newErrors.firmwareVersion =
          "Hardware version is required (e.g., 0.3.0.0)";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fill in all required fields correctly");
      return;
    }

    setIsLoading(true);
    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    try {
      await otaVersionsApi.create({ ...formData, tags });
      toast.success(
        `Version ${formData.versionNumber} created successfully! Greengrass component version registered.`
      );
      onVersionCreated();
      onClose();
    } catch (error: unknown) {
      const err = error as { response?: { data?: Record<string, unknown> } };
      if (err.response?.data) {
        const backendErrors = err.response.data;
        if (typeof backendErrors === "object" && backendErrors !== null) {
          const typedErrors = backendErrors as Record<
            string,
            string | string[]
          >;
          const newErrors: Record<string, string> = {};
          const fieldMapping: Record<string, string> = {
            version_number: "versionNumber",
            s3_uri: "s3Uri",
            sha256_hash: "sha256Hash",
            firmware_version: "firmwareVersion",
            host_package_version: "hostPackageVersion",
            ccm_image_tag: "ccmImageTag",
            component_id: "componentId",
          };
          Object.keys(typedErrors).forEach((key) => {
            const msg = Array.isArray(typedErrors[key])
              ? typedErrors[key][0]
              : String(typedErrors[key]);
            newErrors[fieldMapping[key] || key] = msg;
          });
          setErrors(newErrors);
          if ("non_field_errors" in typedErrors) {
            const nfe = typedErrors.non_field_errors;
            toast.error(Array.isArray(nfe) ? nfe[0] : String(nfe));
          }
        } else {
          toast.error("An error occurred while creating the version");
        }
      } else {
        toast.error("Failed to create component version. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h2>Add Version to {component.name}</h2>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <InfoBox>
          <InfoLabel>AWS Component:</InfoLabel>
          <InfoValue>{component.awsComponentName || "Not set"}</InfoValue>
        </InfoBox>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="versionNumber">
              Version Number <Required>*</Required>
            </Label>
            <Input
              id="versionNumber"
              type="text"
              placeholder="1.0.4"
              value={formData.versionNumber}
              onChange={(e) => handleChange("versionNumber", e.target.value)}
              hasError={!!errors.versionNumber}
              disabled={isLoading}
            />
            {errors.versionNumber && (
              <ErrorText>{errors.versionNumber}</ErrorText>
            )}
            <HelpText>
              Format: d.d.d (e.g. 1.0.4). RC builds: patch 9N (11.0.92 = rc2).
              Master builds: patch 8N (11.4.83 = m3).
            </HelpText>
          </FormGroup>

          {isContainer ? (
            <>
              <SectionLabel>CCM Configuration</SectionLabel>
              <FormGroup>
                <Label htmlFor="ccmImageTag">
                  Image Tag <Required>*</Required>
                </Label>
                <Input
                  id="ccmImageTag"
                  type="text"
                  placeholder="CSfa2e41dc"
                  value={formData.ccmImageTag || ""}
                  onChange={(e) => handleChange("ccmImageTag", e.target.value)}
                  hasError={!!errors.ccmImageTag}
                  disabled={isLoading}
                />
                {errors.ccmImageTag && (
                  <ErrorText>{errors.ccmImageTag}</ErrorText>
                )}
                <HelpText>
                  CS&lt;hash&gt; Docker image tag — used as lock-tag in run.yaml
                </HelpText>
              </FormGroup>
            </>
          ) : isHost ? (
            <>
              <SectionLabel>Host Package</SectionLabel>
              <FormGroup>
                <Label htmlFor="hostPackageVersion">
                  Package Version <Required>*</Required>
                </Label>
                <Input
                  id="hostPackageVersion"
                  type="text"
                  placeholder="1.3.0~rc7"
                  value={formData.hostPackageVersion || ""}
                  onChange={(e) =>
                    handleChange("hostPackageVersion", e.target.value)
                  }
                  hasError={!!errors.hostPackageVersion}
                  disabled={isLoading}
                />
                {errors.hostPackageVersion && (
                  <ErrorText>{errors.hostPackageVersion}</ErrorText>
                )}
                <HelpText>
                  Apt package version passed to{" "}
                  <code>cyngn_host_version &lt;version&gt;</code> on the vehicle
                  (e.g., 1.3.0~rc7, 2.1.0)
                </HelpText>
              </FormGroup>
            </>
          ) : (
            <>
              <FormGroup>
                <Label htmlFor="s3Uri">
                  S3 URI <Required>*</Required>
                </Label>
                <Input
                  id="s3Uri"
                  type="text"
                  placeholder="s3://bucket-name/path/to/artifact.bin"
                  value={formData.s3Uri || ""}
                  onChange={(e) => handleChange("s3Uri", e.target.value)}
                  onBlur={handleS3UriBlur}
                  hasError={!!errors.s3Uri}
                  disabled={isLoading}
                />
                {errors.s3Uri && <ErrorText>{errors.s3Uri}</ErrorText>}
                <HelpText>Full S3 path to your artifact file</HelpText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="sha256Hash">
                  SHA-256 Hash <Required>*</Required>
                </Label>
                <Sha256InputWrapper>
                  <Input
                    id="sha256Hash"
                    type="text"
                    placeholder={
                      isFetchingSha256
                        ? "Fetching from S3…"
                        : "Paste hex (64 chars) or Base64 from S3"
                    }
                    value={formData.sha256Hash || ""}
                    onChange={(e) => handleChange("sha256Hash", e.target.value)}
                    hasError={!!errors.sha256Hash}
                    disabled={isLoading || isFetchingSha256}
                  />
                  {isFetchingSha256 && <Sha256Spinner />}
                </Sha256InputWrapper>
                {errors.sha256Hash && (
                  <ErrorText>{errors.sha256Hash}</ErrorText>
                )}
                {sha256FetchError && !errors.sha256Hash && (
                  <FetchErrorText>{sha256FetchError}</FetchErrorText>
                )}
                <HelpText>
                  Auto-filled when you enter a valid S3 URI above. Supports
                  64-char hex or Base64 from S3 Console.
                </HelpText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="firmwareVersion">
                  Firmware Version <Required>*</Required>
                </Label>
                <Input
                  id="firmwareVersion"
                  type="text"
                  placeholder="0.3.0.0"
                  value={formData.firmwareVersion || ""}
                  onChange={(e) =>
                    handleChange("firmwareVersion", e.target.value)
                  }
                  hasError={!!errors.firmwareVersion}
                  disabled={isLoading}
                />
                {errors.firmwareVersion && (
                  <ErrorText>{errors.firmwareVersion}</ErrorText>
                )}
                <HelpText>
                  Hardware version string (e.g., 0.3.0.0) — used to verify
                  correct firmware is installed.
                </HelpText>
              </FormGroup>
            </>
          )}

          <FormGroup>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Optional — describe this version (e.g. 'June sprint, stable baseline')"
              value={formData.description || ""}
              onChange={(e) => handleChange("description", e.target.value)}
              disabled={isLoading}
              rows={2}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="tags">Tags</Label>
            <Input
              id="tags"
              type="text"
              placeholder="Comma-separated (e.g. stable, june-sprint, rc1)"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              disabled={isLoading}
            />
            <HelpText>
              Used for filtering versions when creating a release.
            </HelpText>
          </FormGroup>

          {isLoading && (
            <LoadingMessage>
              <Spinner />
              <span>Creating version and registering to Greengrass V2...</span>
            </LoadingMessage>
          )}

          <ButtonGroup>
            <CancelButton type="button" onClick={onClose} disabled={isLoading}>
              Cancel
            </CancelButton>
            <SubmitButton
              type="submit"
              disabled={isLoading || isFetchingSha256}
            >
              {isLoading ? "Creating..." : "Create Version"}
            </SubmitButton>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

// ── Styled components ──────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #111827;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background-color: #f3f4f6;
    color: #111827;
  }
`;

const InfoBox = styled.div`
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  padding: 12px 16px;
  margin: 16px 24px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #0c4a6e;
  font-size: 14px;
`;

const InfoValue = styled.span`
  color: #0369a1;
  font-size: 14px;
  font-family: monospace;
`;

const Form = styled.form`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
`;

const Required = styled.span`
  color: #ef4444;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${(props) => (props.hasError ? "#ef4444" : "#d1d5db")};
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#ef4444" : "#55b8b3")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(239, 68, 68, 0.1)" : "rgba(85, 184, 179, 0.1)"};
  }

  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #55b8b3;
    box-shadow: 0 0 0 3px rgba(85, 184, 179, 0.1);
  }

  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  color: #ef4444;
  font-size: 13px;
  font-weight: 500;

  &:before {
    content: "⚠";
    font-size: 14px;
  }
`;

const SectionLabel = styled.p`
  margin: 4px 0 16px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 6px;
`;

const HelpText = styled.span`
  display: block;
  margin-top: 6px;
  color: #6b7280;
  font-size: 13px;
`;

const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  margin-bottom: 20px;
  color: #0369a1;
  font-size: 14px;
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #bae6fd;
  border-top-color: #0284c7;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;

  &:hover:not(:disabled) {
    background-color: #f9fafb;
  }
`;

const SubmitButton = styled(Button)`
  background: #55b8b3;
  border: none;
  color: white;

  &:hover:not(:disabled) {
    background: #3da09b;
  }
`;

const Sha256InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Sha256Spinner = styled.div`
  position: absolute;
  right: 12px;
  width: 16px;
  height: 16px;
  border: 2px solid #bae6fd;
  border-top-color: #0284c7;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
  pointer-events: none;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const FetchErrorText = styled.span`
  display: block;
  margin-top: 6px;
  color: #b45309;
  font-size: 13px;
`;
